import { Platform } from './enum';

export const TEMPLATE_AGENTS = ['nova', 'ember', 'atlas'] as const;
export type TemplateAgent = (typeof TEMPLATE_AGENTS)[number];

/**
 * Which agents may use each template type (the `platform` field).
 *
 * A template belongs to one agent (its `agentType`). A type has a home agent
 * and may be shared with others — each agent keeps its own copies.
 *
 * Mirrors `Backend/src/constant/templateAgents.ts`; keep the two in sync.
 */
// Partial: a type left out here is hidden from every agent's Type dropdown.
const TEMPLATE_TYPE_HOME_AGENT: Partial<Record<Platform, TemplateAgent>> = {
  [Platform.welcomeLead]: 'nova',
  [Platform.confirmation]: 'nova',
  [Platform.followup]: 'nova',
  [Platform.noShow]: 'nova',
  [Platform.noBooking]: 'nova',
  [Platform.noAppointment]: 'nova',
  [Platform.feedback]: 'nova',
  [Platform.noSale]: 'nova',
  [Platform.cancellation]: 'nova',
  [Platform.reEngage]: 'nova',
  [Platform.overdue]: 'atlas',
  // Ember retention types — hidden until they have a trigger (only membership
  // cancellation is wired up, via create-cancellation-lead). Un-comment to
  // offer them again; the types still exist in the enum and backend.
  // [Platform.winbackOne]: 'ember',
  // [Platform.atRisk]: 'ember',
  // [Platform.saveOffer]: 'ember',
  // [Platform.freezeOption]: 'ember',
};

const TEMPLATE_TYPE_SHARED_WITH: Partial<Record<Platform, TemplateAgent[]>> = {
  [Platform.overdue]: ['nova'],
  [Platform.cancellation]: ['ember'],
};

export const isTemplateAgent = (value: unknown): value is TemplateAgent =>
  typeof value === 'string' &&
  (TEMPLATE_AGENTS as readonly string[]).includes(value);

/**
 * Whether a template belongs to `agent`. Untagged templates belong to their
 * type's home agent (same rule as the backend).
 */
export const templateBelongsToAgent = (
  template: { agentType?: string | null; platform?: string | null },
  agent: TemplateAgent
): boolean =>
  (template.agentType ||
    TEMPLATE_TYPE_HOME_AGENT[template.platform as Platform]) === agent;

/**
 * Template types the given agent may use, in `Platform` order. Agents without
 * templates (Clarion, Beacon) get an empty list.
 */
export const templateTypesForAgent = (agent: string | null): Platform[] =>
  isTemplateAgent(agent)
    ? Object.values(Platform).filter(
        (type) =>
          TEMPLATE_TYPE_HOME_AGENT[type] === agent ||
          TEMPLATE_TYPE_SHARED_WITH[type]?.includes(agent)
      )
    : [];

export const templateTypeLabel = (platform: string): string => {
  if (platform === Platform.noBooking) return 'noResponse';
  return platform;
};
